import React, { useState, useEffect } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getSlackChannels, setSlackChannel } from '../../services';
import { setIntegrationAvailable, setIntegrationData } from '../../redux/slices/integrationsSlice';
import { selectAuthToken } from '../../redux/selectors/authSelectors';
import { LoadingButton } from '../LoadingButton';
import {
  StyledModal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  SectionTitle,
  SectionDescription,
  ChannelList,
  ChannelItem,
  ChannelName,
  ChannelDescription,
  EmptyState,
  CancelButton,
  SaveButton
} from './IntegrationModal.styles';

const SlackConfigModal = ({ isOpen, onClose, onSave }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [channels, setChannels] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});

  useEffect(() => {
    if (isOpen) {
      let cancelled = false;
      setIsLoading(true);

      getSlackChannels(token)
        .then((res) => {
          if (cancelled) return;
          if (res && res.channels) {
            const defaultChannels = {};
            res.channels.forEach(({ channelId, applicationChannel }) => {
              if (applicationChannel === true) {
                defaultChannels[channelId] = true;
              }
            });
            setCheckedItems(defaultChannels);
            setChannels(res.channels);
          }
        })
        .catch(() => {
          if (!cancelled) {
            toast.error('Failed to load Slack channels. Please try again.', {
              position: 'top-right',
              autoClose: 4000,
            });
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });

      return () => { cancelled = true; };
    }
  }, [isOpen, token]);

  const handleChange = (event) => {
    setCheckedItems({
      ...checkedItems,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    const selectedChannels = Object.keys(checkedItems).filter(
      (key) => checkedItems[key] === true
    );

    try {
      const status = await setSlackChannel(selectedChannels);
      if (status === true) {
        toast.success('Success: Set channel(s) as indicated', {
          position: 'top-right',
          autoClose: 4000,
        });
        dispatch(setIntegrationAvailable({ id: 'slack', available: true }));
        const selectedChannelsFull = channels.filter(
          (channel) => selectedChannels.indexOf(channel.channelId) > -1
        );
        dispatch(
          setIntegrationData({
            id: 'slack',
            data: { channels: selectedChannelsFull },
          })
        );
        onSave?.();
      } else {
        toast.error('An error occurred saving your channel selection.', {
          position: 'top-right',
          autoClose: 4000,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <StyledModal
      show={isOpen}
      onHide={onClose}
      centered
      size="lg"
    >
        <ModalHeader closeButton>
        <ModalTitle>Configure Slack</ModalTitle>
      </ModalHeader>

      <ModalBody>
        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" size="sm" role="status" style={{ color: '#a3a5a9' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : channels.length > 0 ? (
          <>
            <SectionTitle>Select channels</SectionTitle>
            <SectionDescription>
              Choose which channels will receive shared documents and messages.
            </SectionDescription>
            <ChannelList>
              {channels.map(({ channelId, name, topic, purpose }) => (
                <ChannelItem key={channelId}>
                  <Form.Check type="checkbox" id={channelId} className="w-100">
                    <Form.Check.Input
                      type="checkbox"
                      name={channelId}
                      checked={checkedItems[channelId] || false}
                      onChange={handleChange}
                    />
                    <Form.Check.Label>
                      <ChannelName>{name}</ChannelName>
                      {(topic || purpose) && (
                        <ChannelDescription>
                          {topic}
                          {purpose && purpose !== '' && ` (${purpose})`}
                        </ChannelDescription>
                      )}
                    </Form.Check.Label>
                  </Form.Check>
                </ChannelItem>
              ))}
            </ChannelList>
          </>
        ) : (
          <EmptyState>
            <h5>No channels found</h5>
            <p>
              Slack isn't connected or no channels are available. Check your connection and try again.
            </p>
          </EmptyState>
        )}
      </ModalBody>

      <ModalFooter>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <LoadingButton
          as={SaveButton}
          onClick={handleSave}
          loading={isSaving}
          loadingText="Saving..."
          disabled={isLoading || channels.length === 0}
        >
          Save
        </LoadingButton>
      </ModalFooter>
    </StyledModal>
  );
};

export default SlackConfigModal;
